
import React, { useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import './Contact.css';
import { FaIndustry, FaFileAlt, FaTruck, FaCogs } from "react-icons/fa";

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// ── Reusable file-drop area ──────────────────────────────────────────────────
function FileDropZone({ accept, maxSizeMB, onFileSelect, onFileError, file, label }) {
  const inputRef = useRef();
  const [dragOver, setDragOver] = useState(false);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragOver(false);
      const dropped = e.dataTransfer.files[0];
      if (dropped) validateAndSet(dropped);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [maxSizeMB, onFileSelect, onFileError]
  );

  function validateAndSet(f) {
    if (f.size > maxSizeMB * 1024 * 1024) {
      if (onFileError) onFileError(`File is too large. Maximum size is ${maxSizeMB} MB.`);
      return;
    }
    if (onFileError) onFileError(null);
    onFileSelect(f);
  }

  return (
    <div
      className={`file-drop-zone${dragOver ? ' drag-over' : ''}`}
      onClick={() => inputRef.current.click()}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        style={{ display: 'none' }}
        onChange={(e) => e.target.files[0] && validateAndSet(e.target.files[0])}
      />
      {file ? (
        <div className="file-preview">
          <span className="file-icon">📎</span>
          <span className="file-name">{file.name}</span>
          <span className="file-size">({(file.size / 1024).toFixed(1)} KB)</span>
          <button
            type="button"
            className="file-remove"
            onClick={(e) => { e.stopPropagation(); onFileSelect(null); }}
          >×</button>
        </div>
      ) : (
        <div className="file-placeholder">
          <span>📂</span>
          <p>{label || 'Drag & drop a file here, or click to browse'}</p>
          <small>Accepted: {accept} — Max {maxSizeMB} MB</small>
        </div>
      )}
    </div>
  );
}

// ── Generic Modal wrapper ────────────────────────────────────────────────────
function Modal({ title, onClose, children }) {
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{title}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}

// ── Status helpers ────────────────────────────────────────────────────────────
function StatusMsg({ status, message }) {
  if (!status) return null;
  return (
    <div className={`alert ${status === 'success' ? 'alert-success' : 'alert-error'}`}>
      {status === 'success' ? '✅' : '❌'} {message}
    </div>
  );
}

// ── Drawing Upload Modal ─────────────────────────────────────────────────────
function DrawingModal({ onClose }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', notes: '' });
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [statusMsg, setStatusMsg] = useState('');

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (fileError) return;
    setLoading(true);
    setStatus(null);
    const data = new FormData();
    Object.entries(form).forEach(([k, v]) => data.append(k, v));
    if (file) data.append('file', file);
    try {
      const res = await axios.post(`${API_URL}/api/quote/drawing`, data);
      setStatus('success');
      setStatusMsg(res.data.message);
      setForm({ name: '', email: '', phone: '', notes: '' });
      setFile(null);
    } catch (err) {
      setStatus('error');
      setStatusMsg(err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal title="📋 Upload Your Drawing" onClose={onClose}>
      <StatusMsg status={status} message={statusMsg} />
      {status === 'success' ? (
        <p className="modal-thank-you">Thank you! We'll review your drawing and get back to you shortly.</p>
      ) : (
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>Name *</label>
              <input className="form-control" name="name" required value={form.name} onChange={handleChange} placeholder="Your name" />
            </div>
            <div className="form-group">
              <label>Email *</label>
              <input className="form-control" type="email" name="email" required value={form.email} onChange={handleChange} placeholder="you@example.com" />
            </div>
          </div>
          <div className="form-group">
            <label>Phone</label>
            <input className="form-control" name="phone" value={form.phone} onChange={handleChange} placeholder="+91 98765 43210" />
          </div>
          <div className="form-group">
            <label>Notes / Requirements</label>
            <textarea className="form-control" name="notes" rows="3" value={form.notes} onChange={handleChange} placeholder="Describe your requirements..." />
          </div>
          <div className="form-group">
            <label>Upload Drawing (optional)</label>
            <FileDropZone
              accept=".pdf,.jpg,.jpeg,.png,.dwg,.dxf"
              maxSizeMB={10}
              onFileSelect={setFile}
              onFileError={setFileError}
              file={file}
              label="Drop your drawing here (PDF, JPG, PNG, DWG, DXF — max 10 MB)"
            />
            {fileError && <p className="file-error">{fileError}</p>}
          </div>
          <button type="submit" className="btn-orange modal-submit" disabled={loading || !!fileError}>
            {loading ? <span className="spinner" /> : null}
            {loading ? 'Submitting…' : 'Submit Drawing Request'}
          </button>
        </form>
      )}
    </Modal>
  );
}

// ── Sample Analysis Modal ────────────────────────────────────────────────────
function SampleModal({ onClose }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', description: '', pickupAddress: '' });
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [statusMsg, setStatusMsg] = useState('');

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (fileError) return;
    setLoading(true);
    setStatus(null);
    const data = new FormData();
    Object.entries(form).forEach(([k, v]) => data.append(k, v));
    if (file) data.append('file', file);
    try {
      const res = await axios.post(`${API_URL}/api/quote/sample`, data);
      setStatus('success');
      setStatusMsg(res.data.message);
      setForm({ name: '', email: '', phone: '', description: '', pickupAddress: '' });
      setFile(null);
    } catch (err) {
      setStatus('error');
      setStatusMsg(err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal title="🔬 Send Sample for Analysis" onClose={onClose}>
      <StatusMsg status={status} message={statusMsg} />
      {status === 'success' ? (
        <p className="modal-thank-you">Thank you! Our team will contact you to arrange sample pickup.</p>
      ) : (
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>Name *</label>
              <input className="form-control" name="name" required value={form.name} onChange={handleChange} placeholder="Your name" />
            </div>
            <div className="form-group">
              <label>Email *</label>
              <input className="form-control" type="email" name="email" required value={form.email} onChange={handleChange} placeholder="you@example.com" />
            </div>
          </div>
          <div className="form-group">
            <label>Phone</label>
            <input className="form-control" name="phone" value={form.phone} onChange={handleChange} placeholder="+91 98765 43210" />
          </div>
          <div className="form-group">
            <label>Product / Sample Description *</label>
            <textarea className="form-control" name="description" rows="3" required value={form.description} onChange={handleChange} placeholder="Describe the product or sample..." />
          </div>
          <div className="form-group">
            <label>Upload Reference Image / Document (optional)</label>
            <FileDropZone
              accept=".pdf,.jpg,.jpeg,.png"
              maxSizeMB={5}
              onFileSelect={setFile}
              onFileError={setFileError}
              file={file}
              label="Drop reference image/doc here (PDF, JPG, PNG — max 5 MB)"
            />
            {fileError && <p className="file-error">{fileError}</p>}
          </div>
          <div className="form-group">
            <label>Pickup Address (optional)</label>
            <textarea className="form-control" name="pickupAddress" rows="2" value={form.pickupAddress} onChange={handleChange} placeholder="Your address for pickup..." />
          </div>
          <button type="submit" className="btn-orange modal-submit" disabled={loading || !!fileError}>
            {loading ? <span className="spinner" /> : null}
            {loading ? 'Submitting…' : 'Submit Sample Request'}
          </button>
        </form>
      )}
    </Modal>
  );
}

// ── Technical Quote Modal ────────────────────────────────────────────────────
function TechnicalQuoteModal({ onClose }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', company: '', category: '', quantity: '', specifications: '' });
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [statusMsg, setStatusMsg] = useState('');

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (fileError) return;
    setLoading(true);
    setStatus(null);
    const data = new FormData();
    Object.entries(form).forEach(([k, v]) => data.append(k, v));
    if (file) data.append('file', file);
    try {
      const res = await axios.post(`${API_URL}/api/quote/technical`, data);
      setStatus('success');
      setStatusMsg(res.data.message);
      setForm({ name: '', email: '', phone: '', company: '', category: '', quantity: '', specifications: '' });
      setFile(null);
    } catch (err) {
      setStatus('error');
      setStatusMsg(err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal title="→ Get a Technical Quote" onClose={onClose}>
      <StatusMsg status={status} message={statusMsg} />
      {status === 'success' ? (
        <p className="modal-thank-you">Thank you! Our technical team will send you a detailed quote within 24 hours.</p>
      ) : (
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>Name *</label>
              <input className="form-control" name="name" required value={form.name} onChange={handleChange} placeholder="Your name" />
            </div>
            <div className="form-group">
              <label>Email *</label>
              <input className="form-control" type="email" name="email" required value={form.email} onChange={handleChange} placeholder="you@example.com" />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Phone</label>
              <input className="form-control" name="phone" value={form.phone} onChange={handleChange} placeholder="+91 98765 43210" />
            </div>
            <div className="form-group">
              <label>Company Name</label>
              <input className="form-control" name="company" value={form.company} onChange={handleChange} placeholder="Your company" />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Product Category *</label>
              <select className="form-control" name="category" required value={form.category} onChange={handleChange}>
                <option value="">Select category</option>
                <option value="Electrical Contacts">Electrical Contacts</option>
                <option value="Silver Alloys">Silver Alloys</option>
                <option value="Custom Parts">Custom Parts</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="form-group">
              <label>Quantity Required</label>
              <input className="form-control" name="quantity" value={form.quantity} onChange={handleChange} placeholder="e.g. 500 units" />
            </div>
          </div>
          <div className="form-group">
            <label>Technical Specifications *</label>
            <textarea className="form-control" name="specifications" rows="4" required value={form.specifications} onChange={handleChange} placeholder="Describe your technical requirements, dimensions, tolerances, material grade, etc." />
          </div>
          <div className="form-group">
            <label>Upload Drawing / Spec Sheet (optional)</label>
            <FileDropZone
              accept=".pdf,.jpg,.jpeg,.png,.dwg,.dxf"
              maxSizeMB={10}
              onFileSelect={setFile}
              onFileError={setFileError}
              file={file}
              label="Drop drawing or spec sheet here (PDF, JPG, PNG, DWG, DXF — max 10 MB)"
            />
            {fileError && <p className="file-error">{fileError}</p>}
          </div>
          <button type="submit" className="btn-orange modal-submit" disabled={loading || !!fileError}>
            {loading ? <span className="spinner" /> : null}
            {loading ? 'Submitting…' : 'Get Technical Quote'}
          </button>
        </form>
      )}
    </Modal>
  );
}

// ── Main Contact page ────────────────────────────────────────────────────────
const Contact = () => {

  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', subject: '', message: ''
  });
  const [attachment, setAttachment] = useState(null);
  const [attachmentError, setAttachmentError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [submitMsg, setSubmitMsg] = useState('');

  // Modal state: null | 'drawing' | 'sample' | 'technical'
  const [activeModal, setActiveModal] = useState(null);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (attachmentError) return;
    setSubmitting(true);
    setSubmitStatus(null);

    const data = new FormData();
    Object.entries(formData).forEach(([k, v]) => data.append(k, v));
    if (attachment) data.append('attachment', attachment);

    try {
      const res = await axios.post(`${API_URL}/api/contact`, data);
      setSubmitStatus('success');
      setSubmitMsg(res.data.message);
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      setAttachment(null);
    } catch (err) {
      setSubmitStatus('error');
      setSubmitMsg(err.response?.data?.message || 'Failed to send message. Please call us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="contact-page">

      {/* ── Page Banner ──────────────────────────────────────────────── */}



      <section className="page-banner" style={{
        backgroundImage: `url('Images/contact-banner.png')`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        // marginLeft: '18%',
        // width: '64%',
      }}>
        <div className="container">
          <div className="banner-content">
            <h1>Contact Us</h1>
            <div className="breadcrumb">
              <Link to="/">Home</Link>
              <span> / </span>
              <span>Contact Us</span>
            </div>
          </div>
        </div>

        {/* contact info cards */}
        <section className="contact-section">
          <div className="container">
            <div className="contact-info-grid">

              {/* Address */}
              <div className="contact-info-card">
                <div className="contact-info-icon">
                  <i className="fas fa-location-dot"></i>
                </div>
                <h3>Our Address</h3>
                <p>
                  B-97D, 2nd Floor <br />
                  Mayapuri Industrial Area Phase-1 <br />
                  New Delhi 110064, India
                </p>
              </div>

              {/* Phone */}
              <div className="contact-info-card">
                <div className="contact-info-icon">
                  <i className="fas fa-phone"></i>
                </div>
                <h3>Phone</h3>
                <p><a href="tel:+911141254770">+91 1141254770</a></p>
                <p><a href="tel:+919643547909">+91 8527881902</a></p>
              </div>

              {/* Email */}
              <div className="contact-info-card">
                <div className="contact-info-icon">
                  <i className="fas fa-envelope"></i>
                </div>
                <h3>Email</h3>
                <p><a href="mailto:info.gadelectricalindustries@gmail.com">info.gadelectricalindustries@gmail.com</a></p>
                <p>
                  <a href="mailto:gadelectricalindustries@gmail.com">
                    gadelectricalindustries@gmail.com
                  </a>
                </p>
              </div>

              {/* Working Hours */}
              <div className="contact-info-card">
                <div className="contact-info-icon">
                  <i className="fas fa-clock"></i>
                </div>
                <h3>Working Hours</h3>
                <p>
                  Monday – Saturday <br />
                  9:00 AM – 6:00 PM
                </p>
                <p className="closed-note">Sunday: Closed</p>
              </div>

            </div>
          </div>
        </section>

      </section>

      {/* ── Contact Form + Map ───────────────────────────────────────── */}
      <section className="contact-main-section">
        <div className="container">
          <div className="contact-main-grid">
            {/* Form */}
            <div className="contact-form-col">
              <span className="section-label">Get In Touch</span>
              <h2 className="section-heading">Send Us a Message</h2>
              <p className="form-intro">
                Have a question about our products or need a bulk quote? Fill in the form below
                and our team will get back to you within 24 hours.
              </p>

              <StatusMsg status={submitStatus} message={submitMsg} />

              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Your Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      className="form-control"
                      placeholder="Suraj Sharma"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      className="form-control"
                      placeholder="suraj@example.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="phone">Phone Number</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      className="form-control"
                      placeholder="+91 98765 43210"
                      value={formData.phone}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="subject">Subject *</label>
                    <select
                      id="subject"
                      name="subject"
                      className="form-control"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select a subject</option>
                      <option value="product-inquiry">Product Inquiry</option>
                      <option value="bulk-order">Bulk Order</option>
                      <option value="dealer-enquiry">Dealer Enquiry</option>
                      <option value="support">After-Sales Support</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="message">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    className="form-control"
                    rows="5"
                    placeholder="Tell us how we can help you..."
                    value={formData.message}
                    onChange={handleChange}
                    required
                  ></textarea>
                </div>
                <div className="form-group">
                  <label>Attachment (optional)</label>
                  <FileDropZone
                    accept=".pdf,.jpg,.jpeg,.png,.dwg,.dxf"
                    maxSizeMB={10}
                    onFileSelect={setAttachment}
                    onFileError={setAttachmentError}
                    file={attachment}
                    label="Attach a file (PDF, JPG, PNG, DWG, DXF — max 10 MB)"
                  />
                  {attachmentError && <p className="file-error">{attachmentError}</p>}
                </div>
                <button type="submit" className="btn btn-primary btn-lg contact-submit" disabled={submitting || !!attachmentError}>
                  {submitting ? <><span className="spinner" /> Sending…</> : 'Send Message →'}
                </button>
              </form>
            </div>

            {/* Side Info */}
            <div className="contact-side-col">

              {/* Right Panel */}
              <div className="right-panel">
                {/* Technical Quote */}
                <div className="quote-box">
                  <h3>Request Technical Quote</h3>
                  <button className="btn-outline" onClick={() => setActiveModal('drawing')}>
                    <span>📋</span> Upload Your Drawing
                  </button>
                  <button className="btn-outline" onClick={() => setActiveModal('sample')}>
                    <span>🔬</span> Send Sample for Analysis
                  </button>
                  <button className="btn-orange" onClick={() => setActiveModal('technical')}>
                    <span>→</span> Get a Technical Quote
                  </button>
                </div>
              </div>

              {/* Map */}
            
 <div>
  <div className="map-box">
    <h3>Find Us on the Map</h3>

    <div className="map-placeholder">
      <iframe
        title="GAD Electricals Location"
        src="https://www.google.com/maps?q=28.624833,77.123389&hl=en&z=17&output=embed"
        width="100%"
        height="328"
        style={{ border: 0, borderRadius: '6px' }}
        allowFullScreen
        loading="lazy"
      ></iframe>
    </div>

    <p className="map-address">
      📍 B-97D, 2nd Floor, Mayapuri Industrial Area Phase-1, New Delhi – 110064
    </p>

    <a
      href="https://www.google.com/maps?q=28.624833,77.123389"
      target="_blank"
      rel="noreferrer"
      className="btn-dark-map"
    >
      📍 View on Google Maps
    </a>
  </div>
</div>

            </div>

          </div>
        </div>
      </section>

      <section>
        <div className=" trust-bar">
          <div className="trust-item">
            <FaIndustry className="trust-icon" />
            <span>35+ Years of Experience</span>
          </div>
          <div className="trust-item">
            <FaFileAlt className="trust-icon" />
            <span>MSME &amp; GST Registered</span>
          </div>
          <div className="trust-item">
            <FaTruck className="trust-icon" />
            <span>Pan India Supply</span>
          </div>
          <div className="trust-item">
            <FaCogs className="trust-icon" />
            <span>Custom Contact Solutions</span>
          </div>
        </div>
      </section>

      {/* ── Modals ───────────────────────────────────────────────────── */}
      {activeModal === 'drawing' && <DrawingModal onClose={() => setActiveModal(null)} />}
      {activeModal === 'sample' && <SampleModal onClose={() => setActiveModal(null)} />}
      {activeModal === 'technical' && <TechnicalQuoteModal onClose={() => setActiveModal(null)} />}

    </div>
  );
};

export default Contact;
